import { ComponentProps, ReactElement, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { getElementLineEndpoints } from '@editorUtils'
import { toNumber } from 'lodash-es'
import { Vector3 } from 'three'
import { useGuidToElementIncludingRips, useSceneElement } from '@editorHooks'
import type { DesignFormRootValues } from '../types'
import { matchesRelativePosition } from '../utils'
import SupportGeometrySceneMarker from './SupportGeometrySceneMarker'

type SupportGeometryMarkerProps = ComponentProps<typeof SupportGeometrySceneMarker>

const SupportGeometryMarkerSlot = ({
  properties,
}: {
  properties: SupportGeometryMarkerProps
}): null => {
  useSceneElement({ enabled: true, properties, component: SupportGeometrySceneMarker })
  return null
}

interface Props {
  elementId: string
}

const SupportGeometrySceneMarkerEffect = ({ elementId }: Props): ReactElement => {
  const guidToElement = useGuidToElementIncludingRips()

  const memberCheckSettingsSelectedElement = useWatch<DesignFormRootValues>({
    name: 'elements.0.memberCheckSettingsSelectedElement',
  }) as SettingsOnMember | null
  const supportConfigsForChecks = useWatch<DesignFormRootValues>({
    name: 'elements.0.supportConfigsForChecks',
  }) as SupportConfigForChecks[] | undefined
  const crossSection = useWatch<DesignFormRootValues>({
    name: 'elements.0.crossSection',
  }) as CrossSectionTimberOrCLTOrSteel | null | undefined

  const supportConfigs = useMemo(() => {
    if (memberCheckSettingsSelectedElement == null) return []
    if (!('support_configs' in memberCheckSettingsSelectedElement)) return []
    return memberCheckSettingsSelectedElement.support_configs ?? []
  }, [memberCheckSettingsSelectedElement])

  const markerPropsList = useMemo((): SupportGeometryMarkerProps[] => {
    if (!elementId) return []

    const endpoints = getElementLineEndpoints(guidToElement[elementId])
    if (!endpoints) return []

    const start = new Vector3(endpoints.start.x, endpoints.start.y, endpoints.start.z)
    const end = new Vector3(endpoints.end.x, endpoints.end.y, endpoints.end.z)
    const direction = end.clone().sub(start)
    const axisLength = direction.length()
    if (axisLength <= 0) return []
    const normalizedDirection = direction.clone().normalize()
    const elementHalfHeight =
      crossSection &&
      'shape' in crossSection &&
      crossSection.shape &&
      'height' in crossSection.shape &&
      typeof crossSection.shape.height === 'number'
        ? crossSection.shape.height / 2
        : 0

    return supportConfigs.flatMap(supportConfig => {
      const checkPosition = toNumber(supportConfig.relative_position)
      if (!Number.isFinite(checkPosition)) return []

      const defaults = supportConfigsForChecks?.find(({ relative_position }) =>
        matchesRelativePosition(relative_position, supportConfig.relative_position),
      )

      const area = supportConfig.area ?? defaults?.area
      if (!area || !area.length || !area.width || area.length <= 0 || area.width <= 0) return []

      const overhang = supportConfig.overhang ?? defaults?.overhang

      const center = start
        .clone()
        .add(direction.clone().multiplyScalar(checkPosition))
        .add(new Vector3(0, 0, -elementHalfHeight))

      return [
        {
          center: { x: center.x, y: center.y, z: center.z },
          direction: {
            x: normalizedDirection.x,
            y: normalizedDirection.y,
            z: normalizedDirection.z,
          },
          length: area.length,
          width: area.width,
          overhangLeft: overhang?.left ?? 0,
          overhangRight: overhang?.right ?? 0,
          overhangFront: overhang?.front ?? 0,
          overhangBack: overhang?.back ?? 0,
        },
      ]
    })
  }, [crossSection, elementId, guidToElement, supportConfigs, supportConfigsForChecks])

  return (
    <>
      {markerPropsList.map((properties, index) => (
        <SupportGeometryMarkerSlot key={index} properties={properties} />
      ))}
    </>
  )
}

export default SupportGeometrySceneMarkerEffect
